import { useState, useEffect, useMemo } from 'react';
import { reportDateHelpers } from 'utils/timeZone';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Grid,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  Paper,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Chip,
  Stack,
  useTheme,
  Avatar,
  Alert,
  Tooltip,
  TextField
} from '@mui/material';
import { Container } from '@mui/system';
import { PieChart } from '@mui/x-charts/PieChart';
import { saveAs } from 'file-saver';
import moment from 'moment-timezone';

// Íconos
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TuneIcon from '@mui/icons-material/Tune'; // Custom filter icon
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoIcon from '@mui/icons-material/Info';

import { gridSpacing } from 'store/constant';
import { getApi } from 'views/services/api';
import ExpensesChart from './ExpensesChart';
import ReservedRoomView from './Default/ReservedRoomsTable';

// ==============================|| PÁGINA DE REPORTES GENERALES ||============================== //

const GeneralReports = () => {
  const theme = useTheme();

  // Estados
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // NUEVO: Estado para data cruda
  const [rawAllReservations, setRawAllReservations] = useState([]);
  const [rawFoodInvoices, setRawFoodInvoices] = useState([]);

  // Filter States
  const [filterType, setFilterType] = useState('today'); // 'today' | 'weekly' | 'monthly' | 'yearly' | 'custom'
  const [customDateRange, setCustomDateRange] = useState({
    startDate: moment(),
    endDate: moment()
  });

  const [anchorEl, setAnchorEl] = useState(null);

  // Datos del hotel desde localStorage
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'Rs';

  // Handlers de menú
  const handleExportMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setAnchorEl(null);
  };

  // Traer TODAS las reservaciones (Active, Pending, Completed, etc.)
  const fetchReservationData = async () => {
    try {
      setError(null);
      console.log('Fetching all reservations...');
      // CAMBIO: Usamos el endpoint que sabemos que funciona en el sidebar
      // Aunque se llame "activeandcompleted", parece traer todo sin filtrar status en el backend
      const response = await getApi(`api/reservation/viewactiveandcompletedreservation/${hotel.hotelId}`);
      console.log('All Reservations Response:', response.data);
      setRawAllReservations(response.data.reservationData || []);
    } catch (error) {
      console.error('Error al cargar reservaciones:', error);
      setError('No se pudieron cargar las reservaciones. Inténtalo de nuevo.');
    }
  };

  // Traer facturas de comida separadas
  const fetchBillData = async () => {
    try {
      const response = await getApi(`api/separatefoodinvoice/view/${hotel.hotelId}`);
      setRawFoodInvoices(response?.data?.InvoiceData || []);
    } catch (error) {
      console.error('Error al cargar facturas de comida:', error);
      setError('No se pudieron cargar los datos de comida y bebidas. Inténtalo de nuevo.');
    }
  };

  // Efecto: Cargar datos iniciales (solo una vez o si cambia hotel)
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchReservationData(), fetchBillData()]);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError('No se pudieron cargar los datos. Verifica tu conexión e inténtalo nuevamente.');
      } finally {
        setIsLoading(false);
      }
    };

    if (hotel?.hotelId) {
      fetchData();
    } else {
      setError('No se encontró información del hotel. Por favor, actualiza la página.');
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.hotelId]);

  // ================= CALCULO DE DATOS FILTRADOS =================

  // Helper to get range based on type
  const getEffectiveRange = (type) => {
    if (type === 'custom') {
      return {
        start: customDateRange.startDate ? customDateRange.startDate.clone().startOf('day') : moment().startOf('day'),
        end: customDateRange.endDate ? customDateRange.endDate.clone().endOf('day') : moment().endOf('day')
      };
    }
    return reportDateHelpers.getPeriodRange(type);
  };

  const getEffectivePreviousRange = (type) => {
    if (type === 'custom') {
      // For custom range, we just disable comparison by returning same range or 0 range
      // Or implemented "Previous X Days" logic
      if (!customDateRange.startDate || !customDateRange.endDate) return { start: moment(), end: moment() };

      const duration = customDateRange.endDate.diff(customDateRange.startDate, 'days') + 1;
      const end = customDateRange.startDate.clone().subtract(1, 'day').endOf('day');
      const start = end
        .clone()
        .subtract(duration - 1, 'days')
        .startOf('day');
      return { start, end };
    }
    return reportDateHelpers.getPreviousPeriodRange(type);
  };

  // 1. VENTAS (CHECK-IN) - Periodo Actual
  // Filtramos por fecha de LLEGADA. Incluye activas, completadas, pendientes...
  const salesData = useMemo(() => {
    const { start, end } = getEffectiveRange(filterType);
    return rawAllReservations.filter((item) => reportDateHelpers.isInRange(item.checkInDate, start, end));
  }, [rawAllReservations, filterType, customDateRange]);

  // 2. VENTAS (CHECK-IN) - Periodo Anterior
  const historicalSalesData = useMemo(() => {
    const { start, end } = getEffectivePreviousRange(filterType);
    return rawAllReservations.filter((item) => reportDateHelpers.isInRange(item.checkInDate, start, end));
  }, [rawAllReservations, filterType, customDateRange]);

  // 3. CIERRES (CHECK-OUT) - Periodo Actual
  // Filtramos por fecha de SALIDA y status 'checked-out'
  const closuresData = useMemo(() => {
    const { start, end } = getEffectiveRange(filterType);
    return rawAllReservations.filter((item) => item.status === 'checked-out' && reportDateHelpers.isInRange(item.checkOutDate, start, end));
  }, [rawAllReservations, filterType, customDateRange]);

  // 4. CIERRES (CHECK-OUT) - Periodo Anterior
  const historicalClosuresData = useMemo(() => {
    const { start, end } = getEffectivePreviousRange(filterType);
    return rawAllReservations.filter((item) => item.status === 'checked-out' && reportDateHelpers.isInRange(item.checkOutDate, start, end));
  }, [rawAllReservations, filterType, customDateRange]);

  // 5. COMIDA (SEPARADA) - Periodo Actual y Anterior
  const separateFoodData = useMemo(() => {
    const { start, end } = getEffectiveRange(filterType);
    return rawFoodInvoices.filter((item) => reportDateHelpers.isInRange(item.createdDate, start, end));
  }, [rawFoodInvoices, filterType, customDateRange]);

  const historicalFoodData = useMemo(() => {
    const { start, end } = getEffectivePreviousRange(filterType);
    return rawFoodInvoices.filter((item) => reportDateHelpers.isInRange(item.createdDate, start, end));
  }, [rawFoodInvoices, filterType, customDateRange]);

  // ================= TOTALES GENERALES =================

  const currentPeriodTotals = useMemo(() => {
    // --- MÉTRICAS DE VENTAS (CHECK-IN) ---
    // Suma del total de la reserva (Rooms + Food cargada a la habitación)
    // Nota: Usamos totalPayment que suele ser la suma de todo lo pagado/debido.
    // Si queremos solo habitación, deberíamos ver cómo se estructura 'totalPayment'.
    // Asumiremos que totalPayment refleja el valor de la venta generada hoy.
    const salesTotal = salesData.reduce((sum, r) => sum + (r.totalPayment || 0), 0);
    const salesCount = salesData.length;

    // --- MÉTRICAS DE CIERRES (CHECK-OUT) ---
    const closuresTotal = closuresData.reduce((sum, r) => sum + (r.totalPayment || 0), 0);
    const closuresCount = closuresData.length;

    // --- COMIDA ---
    // Comida dentro de reservas de CIERRE (ya cobrada/finalizada)
    const foodInClosures = closuresData.reduce((total, reservation) => {
      if (reservation.foodItems && reservation.foodItems.length > 0) {
        return total + reservation.foodItems.reduce((foodTotal, food) => foodTotal + (food.price || 0) * (food.quantity || 0), 0);
      }
      return total;
    }, 0);

    // Comida separada
    const separateFoodTotal = separateFoodData.reduce((total, item) => total + (item.totalAmount || 0), 0);

    const totalFoodIncome = separateFoodTotal + foodInClosures;

    // TOTAL FINAL "CAJA" (Cierres + Comida Separada)
    // Este es el dinero que "cierra" hoy.
    const totalIncome = closuresTotal + separateFoodTotal;
    // Nota: salesTotal no se suma aquí porque es "Venta Teórica/Future", salvo que sea prepago.
    // Pero para el reporte financiero de "Cierre", mantenemos la lógica anterior de Cierres + Comida.

    return {
      salesTotal,
      salesCount,
      closuresTotal,
      closuresCount,
      totalFoodIncome,
      totalIncome, // Defined as Closures + Separate Food (Realized Income)
      separateFoodTotal,
      foodOrdersCount: separateFoodData.length
    };
  }, [salesData, closuresData, separateFoodData]);

  const previousPeriodTotals = useMemo(() => {
    const salesTotal = historicalSalesData.reduce((sum, r) => sum + (r.totalPayment || 0), 0);
    const closuresTotal = historicalClosuresData.reduce((sum, r) => sum + (r.totalPayment || 0), 0);

    const foodInClosures = historicalClosuresData.reduce((total, reservation) => {
      if (reservation.foodItems && reservation.foodItems.length > 0) {
        return total + reservation.foodItems.reduce((foodTotal, food) => foodTotal + (food.price || 0) * (food.quantity || 0), 0);
      }
      return total;
    }, 0);

    const separateFoodTotal = historicalFoodData.reduce((total, item) => total + (item.totalAmount || 0), 0);
    const totalIncome = closuresTotal + separateFoodTotal;

    return {
      salesTotal,
      closuresTotal,
      totalIncome,
      totalFoodIncome: foodInClosures + separateFoodTotal
    };
  }, [historicalSalesData, historicalClosuresData, historicalFoodData]);

  // Cambios porcentuales
  const percentageChanges = useMemo(() => {
    const calculateChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    const totalIncomeChange = calculateChange(currentPeriodTotals.totalIncome, previousPeriodTotals.totalIncome);
    const salesChange = calculateChange(currentPeriodTotals.salesTotal, previousPeriodTotals.salesTotal);
    const closuresChange = calculateChange(currentPeriodTotals.closuresTotal, previousPeriodTotals.closuresTotal);
    const foodChange = calculateChange(currentPeriodTotals.totalFoodIncome, previousPeriodTotals.totalFoodIncome);

    const getTrend = (val) => (val > 0 ? 'up' : val < 0 ? 'down' : 'flat');

    return {
      totalIncome: { value: totalIncomeChange, trend: getTrend(totalIncomeChange) },
      sales: { value: salesChange, trend: getTrend(salesChange) },
      closures: { value: closuresChange, trend: getTrend(closuresChange) },
      food: { value: foodChange, trend: getTrend(foodChange) }
    };
  }, [currentPeriodTotals, previousPeriodTotals]);

  // Datos para el gráfico
  const pieChartIncomeData = useMemo(() => {
    if (currentPeriodTotals.totalIncome === 0) return [];
    return [
      { id: 0, value: currentPeriodTotals.closuresTotal, label: 'Habitaciones (Cierres)', color: theme.palette.primary.main },
      { id: 1, value: currentPeriodTotals.totalFoodIncome, label: 'Comidas y Bebidas', color: theme.palette.warning.main }
    ].filter((item) => item.value > 0);
  }, [currentPeriodTotals, theme.palette]);

  // Exportar e imprimir
  const exportToCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const periodText = getReadablePeriod();

    const csvData = [
      ['Reporte Financiero', `${hotel?.name || 'Hotel'} - ${periodText}`],
      ['Generado el', new Date().toLocaleString()],
      [''],
      ['RESUMEN GENERAL'],
      ['Ingresos Totales (Caja/Cierre)', `${currency} ${currentPeriodTotals.totalIncome.toFixed(2)}`],
      ['Ventas Nuevas (Check-in)', `${currency} ${currentPeriodTotals.salesTotal.toFixed(2)}`],
      [''],
      ['DETALLE DE CIERRES (Ingresos Reales)'],
      ['Habitaciones Facturadas', `${currency} ${currentPeriodTotals.closuresTotal.toFixed(2)}`],
      ['Comidas y Bebidas', `${currency} ${currentPeriodTotals.totalFoodIncome.toFixed(2)}`],
      [''],
      ['ESTADÍSTICAS'],
      ['Estancias Cerradas', currentPeriodTotals.closuresCount],
      ['Nuevas Llegadas', currentPeriodTotals.salesCount],
      ['Órdenes de Comida', currentPeriodTotals.foodOrdersCount],
      [''],
      ['TENDENCIAS (vs período anterior)'],
      ['Cambio en Caja Total', `${percentageChanges.totalIncome.value.toFixed(1)}%`],
      ['Cambio en Ventas Nuevas', `${percentageChanges.sales.value.toFixed(1)}%`],
      ['Cambio en Cierres', `${percentageChanges.closures.value.toFixed(1)}%`]
    ];

    const csvContent = csvData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `reporte_financiero_${filterType}_${timestamp}.csv`);
    handleExportMenuClose();
  };

  const printPieChart = () => {
    const printWindow = window.open('', '_blank');
    const periodText = getReadablePeriod();

    printWindow.document.write(`
      <html>
        <head>
          <title>Reporte Financiero - ${periodText}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #1976d2; padding-bottom: 20px; }
            .section { margin: 20px 0; padding: 15px; background: #f9f9f9; border-radius: 8px; border: 1px solid #eee; }
            .metric { display: flex; justify-content: space-between; margin: 8px 0; padding: 8px; background: white; border-radius: 4px; }
            .highlight { font-weight: bold; color: #1565c0; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #777; border-top: 1px solid #ddd; padding-top: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Reporte Financiero</h1>
            <h2>${hotel?.name || 'Hotel Management System'}</h2>
            <p>${periodText} - ${getDateRangeText()}</p>
          </div>
          
          <div class="section">
            <h3>Resumen de Caja (Cierres + Comida)</h3>
            <div class="metric">
              <span><strong>Ingresos Totales (Realizados):</strong></span>
              <span class="highlight">${currency} ${currentPeriodTotals.totalIncome.toFixed(2)}</span>
            </div>
          </div>

          <div class="section">
            <h3>Ventas / Producción (Check-ins)</h3>
            <div class="metric">
              <span>Ventas Nuevas:</span>
              <span>${currency} ${currentPeriodTotals.salesTotal.toFixed(2)}</span>
            </div>
            <div class="metric">
              <span>Nuevas Llegadas:</span>
              <span>${currentPeriodTotals.salesCount}</span>
            </div>
          </div>
          
          <div class="section">
            <h3>Desglose de Cierres</h3>
            <div class="metric">
              <span>Habitaciones Facturadas (${currentPeriodTotals.closuresCount}):</span>
              <span>${currency} ${currentPeriodTotals.closuresTotal.toFixed(2)}</span>
            </div>
            <div class="metric">
              <span>Comidas y Bebidas (${currentPeriodTotals.foodOrdersCount}):</span>
              <span>${currency} ${currentPeriodTotals.totalFoodIncome.toFixed(2)}</span>
            </div>
          </div>
          
          <div class="footer">
            <p>Generado el ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
    handleExportMenuClose();
  };

  // Ícono de tendencia
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon fontSize="small" />;
      case 'down':
        return <TrendingDownIcon fontSize="small" />;
      default:
        return <TrendingFlatIcon fontSize="small" />;
    }
  };

  // Color de tendencia
  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up':
        return 'success';
      case 'down':
        return 'error';
      default:
        return 'default';
    }
  };

  // Texto legible del período (para CSV/impresión)
  const getReadablePeriod = () => {
    switch (filterType) {
      case 'today':
        return 'Hoy';
      case 'weekly':
        return 'Esta semana';
      case 'monthly':
        return 'Este mes';
      case 'yearly':
        return 'Este año';
      default:
        return 'Todo el tiempo';
    }
  };

  // Rango de fechas formateado
  // Rango de fechas formateado
  const getDateRangeText = () => {
    const { start, end } = getEffectiveRange(filterType);

    switch (filterType) {
      case 'today':
        return `Hoy (${start.format('DD/MM/YYYY')})`;
      case 'weekly':
        return `Esta semana (${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')})`;
      case 'monthly':
        return `Este mes (${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')})`;
      case 'yearly':
        return `Este año (${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')})`;
      case 'custom':
        return `Personalizado (${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')})`;
      default:
        return 'Todo el tiempo';
    }
  };

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        py: 3
      }}
    >
      <Container maxWidth="xl">
        {/* Encabezado */}
        <Box
          sx={{
            mb: 4,
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px dashed #e0e0e0`
          }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 0.5
              }}
            >
              Reportes Financieros
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {getDateRangeText()}
            </Typography>
            {previousPeriodTotals.totalIncome > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <InfoIcon fontSize="small" sx={{ mr: 0.5 }} />
                Comparando con el período anterior ({getReadablePeriod().toLowerCase()})
              </Typography>
            )}
          </Box>

          <Box display="flex" alignItems="center">
            <Box
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 2,
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                p: 0.5,
                display: 'flex',
                mr: 2
              }}
            >
              <Button
                variant={filterType === 'today' ? 'contained' : 'text'}
                color="primary"
                onClick={() => setFilterType('today')}
                size="small"
                startIcon={<CalendarTodayIcon />}
                sx={{
                  borderRadius: 1.5,
                  minWidth: 100,
                  ...(filterType !== 'today' && {
                    color: 'text.primary'
                  })
                }}
              >
                Hoy
              </Button>
              <Button
                variant={filterType === 'weekly' ? 'contained' : 'text'}
                color="primary"
                onClick={() => setFilterType('weekly')}
                size="small"
                startIcon={<DateRangeIcon />}
                sx={{
                  borderRadius: 1.5,
                  minWidth: 100,
                  ...(filterType !== 'weekly' && {
                    color: 'text.primary'
                  })
                }}
              >
                Semana
              </Button>
              <Button
                variant={filterType === 'monthly' ? 'contained' : 'text'}
                color="primary"
                onClick={() => setFilterType('monthly')}
                size="small"
                startIcon={<EventIcon />}
                sx={{
                  borderRadius: 1.5,
                  minWidth: 100,
                  ...(filterType !== 'monthly' && {
                    color: 'text.primary'
                  })
                }}
              >
                Mes
              </Button>
              <Button
                variant={filterType === 'yearly' ? 'contained' : 'text'}
                color="primary"
                onClick={() => setFilterType('yearly')}
                size="small"
                startIcon={<CalendarMonthIcon />}
                sx={{
                  borderRadius: 1.5,
                  minWidth: 100,
                  ...(filterType !== 'yearly' && {
                    color: 'text.primary'
                  })
                }}
              >
                Año
              </Button>
              <Button
                variant={filterType === 'custom' ? 'contained' : 'text'}
                color="primary"
                onClick={() => setFilterType('custom')}
                size="small"
                startIcon={<TuneIcon />}
                sx={{
                  borderRadius: 1.5,
                  minWidth: 100,
                  ...(filterType !== 'custom' && {
                    color: 'text.primary'
                  })
                }}
              >
                Personalizado
              </Button>
            </Box>

            {/* CUSTOM DATE RANGE PICKERS */}
            {filterType === 'custom' && (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mr: 2,
                    bgcolor: 'white',
                    p: 0.5,
                    borderRadius: 2,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
                  }}
                >
                  <DatePicker
                    label="Desde"
                    value={customDateRange.startDate}
                    onChange={(newValue) => setCustomDateRange((prev) => ({ ...prev, startDate: newValue }))}
                    slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                  />
                  <DatePicker
                    label="Hasta"
                    value={customDateRange.endDate}
                    onChange={(newValue) => setCustomDateRange((prev) => ({ ...prev, endDate: newValue }))}
                    slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                  />
                </Box>
              </LocalizationProvider>
            )}

            <Button
              variant="outlined"
              color="primary"
              onClick={handleExportMenuOpen}
              startIcon={<FileDownloadIcon />}
              sx={{ borderRadius: 2 }}
            >
              Exportar
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleExportMenuClose}
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <MenuItem onClick={exportToCSV} sx={{ py: 1.5 }}>
                <FileDownloadIcon fontSize="small" sx={{ mr: 1.5 }} />
                Exportar a CSV
              </MenuItem>
              <MenuItem onClick={printPieChart} sx={{ py: 1.5 }}>
                <PrintIcon fontSize="small" sx={{ mr: 1.5 }} />
                Imprimir reporte
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Tarjetas de resumen */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* 1. NUEVO: VENTAS (CHECK-IN) */}
          <Grid item xs={12} md={3}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                height: '100%',
                background: 'linear-gradient(45deg, #ffffff 0%, #f3e5f5 100%)', // Purple tint
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'rgba(156, 39, 176, 0.07)',
                  zIndex: 0
                }}
              />
              <Box position="relative" zIndex={1}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', color: '#7b1fa2', mr: 1 }}>
                    INGRESOS POR PAGOS
                  </Typography>
                  <Tooltip
                    title="Muestra el dinero que ingresó, según la fecha en que los huéspedes realizaron el pago, sin importar cuándo fue o será la estancia."
                    placement="top"
                  >
                    <InfoIcon fontSize="small" sx={{ color: '#7b1fa2', cursor: 'pointer', fontSize: 16 }} />
                  </Tooltip>
                </Box>
                {isLoading ? (
                  <Skeleton variant="text" width="60%" height={50} />
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#4a148c' }}>
                    {currency} {currentPeriodTotals.salesTotal.toLocaleString()}
                  </Typography>
                )}
                {!isLoading && (
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      <strong>{currentPeriodTotals.salesCount}</strong> nuevas llegadas
                    </Typography>
                    {previousPeriodTotals.salesTotal > 0 && (
                      <Chip
                        icon={getTrendIcon(percentageChanges.sales.trend)}
                        label={`${Math.abs(percentageChanges.sales.value).toFixed(0)}%`}
                        color="secondary" // Purple
                        size="small"
                        sx={{ height: 20, ml: 1 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* 2. CAJA TOTAL (CIERRES) - Antes Ingresos Totales */}
          <Grid item xs={12} md={3}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                height: '100%',
                background: 'linear-gradient(45deg, #ffffff 0%, #f4f7fe 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'rgba(63, 81, 181, 0.07)',
                  zIndex: 0
                }}
              />

              <Box position="relative" zIndex={1}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
                    INGRESOS POR CHECK-OUT
                  </Typography>
                  <Tooltip
                    title="Muestra los ingresos correspondientes a las reservas que finalizaron (checkout) en la fecha seleccionada, independientemente de cuándo se haya realizado el pago"
                    placement="top"
                  >
                    <InfoIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'pointer', fontSize: 16 }} />
                  </Tooltip>
                </Box>

                {isLoading ? (
                  <Skeleton variant="text" width="60%" height={50} />
                ) : (
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 2
                    }}
                  >
                    {currency} {currentPeriodTotals.totalIncome.toLocaleString()}
                  </Typography>
                )}

                {!isLoading && previousPeriodTotals.totalIncome > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      icon={getTrendIcon(percentageChanges.totalIncome.trend)}
                      label={`${Math.abs(percentageChanges.totalIncome.value).toFixed(1)}%`}
                      color={getTrendColor(percentageChanges.totalIncome.trend)}
                      size="small"
                      sx={{ height: 24 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      vs. anterior
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* 3. HABITACIONES (CIERRES) */}
          <Grid item xs={12} md={3}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                height: '100%',
                background: 'linear-gradient(45deg, #ffffff 0%, #e8f0fe 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'rgba(33, 150, 243, 0.07)',
                  zIndex: 0
                }}
              />

              <Box position="relative" zIndex={1}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  HABITACIONES FACTURADAS
                </Typography>

                {isLoading ? (
                  <Skeleton variant="text" width="60%" height={50} />
                ) : (
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#1976d2',
                      mb: 2
                    }}
                  >
                    {currency} {currentPeriodTotals.closuresTotal.toLocaleString()}
                  </Typography>
                )}

                {!isLoading && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{currentPeriodTotals.closuresCount}</strong> cierres
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* 4. COMIDA (CIERRES) */}
          <Grid item xs={12} md={3}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                height: '100%',
                background: 'linear-gradient(45deg, #ffffff 0%, #fff8e1 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'rgba(255, 152, 0, 0.07)',
                  zIndex: 0
                }}
              />

              <Box position="relative" zIndex={1}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  COMIDAS Y BEBIDAS
                </Typography>

                {isLoading ? (
                  <Skeleton variant="text" width="60%" height={50} />
                ) : (
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#ed6c02',
                      mb: 2
                    }}
                  >
                    {currency} {currentPeriodTotals.totalFoodIncome.toLocaleString()}
                  </Typography>
                )}

                {!isLoading && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{currentPeriodTotals.foodOrdersCount}</strong> órdenes
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Sección gráficos de ingresos */}
          <Grid item xs={12} md={7}>
            <Paper
              sx={{
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                height: '100%',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid rgba(0,0,0,0.07)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <AttachMoneyIcon sx={{ mr: 1, color: '#1976d2' }} />
                  Distribución de Ingresos
                </Typography>
              </Box>

              <Box id="pie-chart-div" sx={{ px: 3, py: 2 }}>
                {isLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                    <Skeleton variant="circular" width={300} height={300} />
                  </Box>
                ) : currentPeriodTotals.totalIncome === 0 ? (
                  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={350} textAlign="center">
                    <AttachMoneyIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Sin ingresos registrados
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      No hay datos financieros para el período seleccionado.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <PieChart
                        series={[
                          {
                            data: pieChartIncomeData,
                            innerRadius: 85,
                            paddingAngle: 2,
                            cornerRadius: 4,
                            highlightScope: { faded: 'global', highlighted: 'item' },
                            faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' }
                          }
                        ]}
                        width={500}
                        height={350}
                        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                        slotProps={{
                          legend: {
                            direction: 'row',
                            position: { vertical: 'bottom', horizontal: 'middle' },
                            padding: 0
                          }
                        }}
                      />
                    </Box>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            backgroundColor: '#e3f2fd',
                            border: '1px solid rgba(33, 150, 243, 0.1)',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: '#1976d2',
                              mr: 2
                            }}
                          >
                            <HotelIcon />
                          </Avatar>

                          <Box>
                            <Typography variant="overline" display="block" color="text.secondary">
                              Reservas de Habitaciones
                            </Typography>
                            <Typography variant="h5" fontWeight={600}>
                              {currency} {currentPeriodTotals.closuresTotal.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {currentPeriodTotals.closuresCount} reservas
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            backgroundColor: '#fff8e1',
                            border: '1px solid rgba(255, 152, 0, 0.1)',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: '#ed6c02',
                              mr: 2
                            }}
                          >
                            <RestaurantIcon />
                          </Avatar>

                          <Box>
                            <Typography variant="overline" display="block" color="text.secondary">
                              Comidas y Bebidas
                            </Typography>
                            <Typography variant="h5" fontWeight={600}>
                              {currency} {currentPeriodTotals.totalFoodIncome.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {currentPeriodTotals.foodOrdersCount} órdenes
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Sección de habitaciones reservadas */}
          <Grid item xs={12} md={5}>
            <Paper
              sx={{
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                height: '100%'
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid rgba(0,0,0,0.07)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <HotelIcon sx={{ mr: 1, color: '#1976d2' }} />
                  Habitaciones Reservadas
                </Typography>

                <IconButton size="small">
                  <MoreVertIcon />
                </IconButton>
              </Box>

              <Box sx={{ p: 0 }}>
                <ReservedRoomView />
              </Box>
            </Paper>
          </Grid>

          {/* Sección de gastos */}
          <Grid item xs={12}>
            <Paper
              sx={{
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid rgba(0,0,0,0.07)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <TrendingUpIcon sx={{ mr: 1, color: '#4caf50' }} />
                  Resumen de Gastos
                </Typography>

                <IconButton size="small">
                  <MoreVertIcon />
                </IconButton>
              </Box>

              <Box sx={{ p: 2 }}>
                <ExpensesChart />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default GeneralReports;
